import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Plus, Filter, Download, Eye, Edit, Search, MapPin, Building, X, ChevronRight, ChevronLeft, Save, CheckCircle2, AlertTriangle, Trash2 } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { MultiSelect } from '../components/ui/MultiSelect';
import { Textarea } from '../components/ui/Textarea';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data service would be imported
const initialClientsData = mockData.getClients();
const columnsConfig = [{
  key: 'number',
  label: 'N° Cliente'
}, {
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'zone',
  label: 'Zona'
}, {
  key: 'clientOf',
  label: 'Cliente di'
}, {
  key: 'safetyReferent',
  label: 'Ref. sicurezza'
}, {
  key: 'employeesCount',
  label: 'Dipendenti'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function ClientsPage() {
  const navigate = useNavigate();
  const {
    canCreate,
    canEdit,
    canDelete,
    currentUser
  } = useRBAC();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  // Hooks
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialClientsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult,
    isSearching
  } = useSearch(filterResult, ['name', 'number', 'email', 'vatNumber']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (client: any) => {
    setEditingClient(client);
    setFormData(client);
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setEditingClient(null);
    setFormData({
      status: 'prospect',
      clientOf: [],
      zone: '',
      addresses: [],
      number: '' // Will be pre-filled by ProgressiveNumberInput
    });
    setCurrentStep(1);
    setIsModalOpen(true);
  };
  const handleSave = () => {
    // Logic to save client
    console.log('Saving client:', formData);
    setSaveSuccess(true);
    // Show success animation then close
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  const handleDeleteClick = (e: React.MouseEvent, client: any) => {
    e.stopPropagation();
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };
  const handleConfirmDelete = () => {
    console.log('Deleting client:', clientToDelete);
    setShowDeleteDialog(false);
    setClientToDelete(null);
    // Would trigger actual delete here
  };
  const hasActiveFilters = Object.keys(filters).some(key => filters[key]);
  const renderWizardStep = () => {
    switch (currentStep) {
      case 1:
        // Dati Principali
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-841">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-842">
              <Input label="Denominazione / Ragione sociale" placeholder="Es. Azienda SRL" value={formData.name || ''} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} required className="col-span-full" data-id="element-843" />

              <ProgressiveNumberInput type="client" value={formData.number} onChange={val => setFormData({
              ...formData,
              number: val
            })} disabled={false} data-id="element-844" />

              <div className="space-y-1" data-id="element-845">
                <Select label="Stato anagrafica" value={formData.status || 'prospect'} onChange={e => setFormData({
                ...formData,
                status: e.target.value
              })} options={[{
                value: 'prospect',
                label: 'Prospect'
              }, {
                value: 'active',
                label: 'Attivo'
              }, {
                value: 'inactive',
                label: 'Non attivo'
              }, {
                value: 'draft',
                label: 'Bozza'
              }]} required disabled={!canEdit('clients', 'status')} data-id="element-846" />
                {formData.status === 'inactive' && <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200 flex items-center gap-1" data-id="element-847">
                    <AlertTriangle className="h-3 w-3" data-id="element-848" />
                    Attenzione: Automazioni disabilitate
                  </div>}
              </div>

              <div className="space-y-2" data-id="element-849">
                <MultiSelect label="Cliente di" options={[{
                value: 'sef_studio',
                label: 'Sef Studio'
              }, {
                value: 'seform',
                label: 'Seform'
              }]} value={formData.clientOf || []} onChange={val => setFormData({
                ...formData,
                clientOf: val
              })} placeholder="Seleziona..." data-id="element-850" />
                {/* Show selected items highlighted */}
                {formData.clientOf && formData.clientOf.length > 0 && <div className="flex gap-2 flex-wrap" data-id="element-851">
                    {formData.clientOf.map((item: string) => <span key={item} className="inline-flex items-center gap-1 px-2 py-1 bg-seform-orange/10 text-seform-orange text-xs font-medium rounded-full border border-seform-orange/20" data-id="element-852">
                        {item === 'sef_studio' ? 'Sef Studio' : 'Seform'}
                        <button type="button" onClick={() => setFormData({
                    ...formData,
                    clientOf: formData.clientOf.filter((v: string) => v !== item)
                  })} className="hover:text-seform-orange/70" data-id="element-853">
                          <X className="h-3 w-3" data-id="element-854" />
                        </button>
                      </span>)}
                  </div>}
              </div>
            </div>
          </div>;
      case 2:
        // Indirizzi
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-855">
            <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider border-b border-slate-100 pb-2" data-id="element-856">
              Indirizzo operativo
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-857">
              <Input label="Via" value={formData.address?.street || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                street: e.target.value
              }
            })} className="col-span-full" data-id="element-858" />
              <Input label="Civico" value={formData.address?.number || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                number: e.target.value
              }
            })} data-id="element-859" />
              <Input label="CAP" value={formData.address?.zip || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                zip: e.target.value
              }
            })} data-id="element-860" />
              <Input label="Comune" value={formData.address?.city || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                city: e.target.value
              }
            })} data-id="element-861" />
              <Input label="Provincia (2 lettere)" value={formData.address?.province || ''} onChange={e => setFormData({
              ...formData,
              address: {
                ...formData.address,
                province: e.target.value
              }
            })} maxLength={2} data-id="element-862" />
            </div>

            <Select label="Zona" value={formData.zone || ''} onChange={e => setFormData({
            ...formData,
            zone: e.target.value
          })} options={[{
            value: 'arezzo',
            label: 'Arezzo'
          }, {
            value: 'valdarno',
            label: 'Valdarno'
          }, {
            value: 'casentino',
            label: 'Casentino'
          }, {
            value: 'valdichiana',
            label: 'Valdichiana'
          }, {
            value: 'valtiberina',
            label: 'Valtiberina'
          }, {
            value: 'firenze',
            label: 'Firenze'
          }, {
            value: 'siena',
            label: 'Siena'
          }, {
            value: 'perugia',
            label: 'Perugia'
          }]} required data-id="element-863" />

            <div className="flex items-center gap-2 py-2" data-id="element-864">
              <input type="checkbox" id="sameAddress" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={formData.sameLegalAddress} onChange={e => setFormData({
              ...formData,
              sameLegalAddress: e.target.checked
            })} data-id="element-865" />
              <label htmlFor="sameAddress" className="text-sm text-slate-700" data-id="element-866">
                Sede legale uguale a indirizzo operativo
              </label>
            </div>

            {!formData.sameLegalAddress && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-867">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4" data-id="element-868">
                  Sede legale
                </h4>
                <Textarea label="Indirizzo completo" value={formData.legalAddress || ''} onChange={e => setFormData({
              ...formData,
              legalAddress: e.target.value
            })} data-id="element-869" />
              </div>}
          </div>;
      case 3:
        // Contatti
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-870">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-871">
              <Input label="Telefono aziendale" type="tel" value={formData.phone || ''} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} data-id="element-872" />
              <Input label="Email amministrativa" type="email" value={formData.emailAdmin || ''} onChange={e => setFormData({
              ...formData,
              emailAdmin: e.target.value
            })} required data-id="element-873" />
              <Input label="Email consulenza" type="email" value={formData.emailConsulting || ''} onChange={e => setFormData({
              ...formData,
              emailConsulting: e.target.value
            })} data-id="element-874" />
              <Input label="Email formazione" type="email" value={formData.emailTraining || ''} onChange={e => setFormData({
              ...formData,
              emailTraining: e.target.value
            })} data-id="element-875" />
              <Input label="PEC" type="email" value={formData.pec || ''} onChange={e => setFormData({
              ...formData,
              pec: e.target.value
            })} data-id="element-876" />
              <Input label="Sito internet" type="url" value={formData.website || ''} onChange={e => setFormData({
              ...formData,
              website: e.target.value
            })} data-id="element-877" />
            </div>
            <div className="h-px bg-slate-100 my-4" data-id="element-878"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-879">
              <Input label="Persona di riferimento" value={formData.contactPerson || ''} onChange={e => setFormData({
              ...formData,
              contactPerson: e.target.value
            })} data-id="element-880" />
              <Input label="Cellulare referente" type="tel" value={formData.contactPhone || ''} onChange={e => setFormData({
              ...formData,
              contactPhone: e.target.value
            })} data-id="element-881" />
            </div>
          </div>;
      case 4:
        // Dati Fiscali
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-882">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-883">
              <Input label="Partita IVA" value={formData.vatNumber || ''} onChange={e => setFormData({
              ...formData,
              vatNumber: e.target.value
            })} maxLength={11} data-id="element-884" />
              <Input label="Codice fiscale" value={formData.fiscalCode || ''} onChange={e => setFormData({
              ...formData,
              fiscalCode: e.target.value
            })} maxLength={16} data-id="element-885" />
              <Input label="Codice SDI" value={formData.sdiCode || ''} onChange={e => setFormData({
              ...formData,
              sdiCode: e.target.value
            })} maxLength={7} data-id="element-886" />
              <Input label="Codice Ateco" value={formData.atecoCode || ''} onChange={e => setFormData({
              ...formData,
              atecoCode: e.target.value
            })} data-id="element-887" />
            </div>
            <Textarea label="Attività aziendale" value={formData.activityDescription || ''} onChange={e => setFormData({
            ...formData,
            activityDescription: e.target.value
          })} data-id="element-888" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-889">
              <Input label="Legale rappresentante" value={formData.legalRep || ''} onChange={e => setFormData({
              ...formData,
              legalRep: e.target.value
            })} data-id="element-890" />
              <Input label="Datore di lavoro" value={formData.employer || ''} onChange={e => setFormData({
              ...formData,
              employer: e.target.value
            })} data-id="element-891" />
            </div>
          </div>;
      case 5:
        // Relazioni
        return <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-892">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-893">
              <Select label="Provenienza cliente" value={formData.source || ''} onChange={e => setFormData({
              ...formData,
              source: e.target.value
            })} options={[{
              value: 'web',
              label: 'Web / Sito'
            }, {
              value: 'fiera',
              label: 'Fiera'
            }, {
              value: 'passaparola',
              label: 'Passaparola'
            }, {
              value: 'partner',
              label: 'Partner'
            }]} data-id="element-894" />
              <Select label="Referente sicurezza (interno)" value={formData.safetyReferent || ''} onChange={e => setFormData({
              ...formData,
              safetyReferent: e.target.value
            })} options={[{
              value: 'alessio',
              label: 'Alessio'
            }, {
              value: 'gianfranco',
              label: 'Gian Franco'
            }, {
              value: 'lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'tiziano',
              label: 'Tiziano'
            }]} required disabled={!canEdit('clients', 'safetyReferent')} data-id="element-895" />
              <Select label="Commercialista" value={formData.accountant || ''} onChange={e => setFormData({
              ...formData,
              accountant: e.target.value
            })} options={[{
              value: 'studio1',
              label: 'Studio Rossi'
            }, {
              value: 'studio2',
              label: 'Studio Bianchi'
            }]} data-id="element-896" />
              <Select label="Consulente del lavoro" value={formData.laborConsultant || ''} onChange={e => setFormData({
              ...formData,
              laborConsultant: e.target.value
            })} options={[{
              value: 'studio1',
              label: 'Studio Rossi'
            }, {
              value: 'studio2',
              label: 'Studio Bianchi'
            }]} data-id="element-897" />
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex justify-between items-center" data-id="element-898">
              <div data-id="element-899">
                <h4 className="font-bold text-seform-navy" data-id="element-900">
                  Dipendenti censiti
                </h4>
                <p className="text-sm text-slate-500" data-id="element-901">
                  Calcolato automaticamente dal modulo Lavoratori
                </p>
              </div>
              <div className="text-3xl font-bold text-seform-orange" data-id="element-902">
                {formData.employeesCount || 0}
              </div>
            </div>
          </div>;
    }
  };
  return <Layout data-id="element-903">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-904">
        <div data-id="element-905">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-906">
            Anagrafica clienti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-907">
            Gestione completa delle aziende clienti e prospect.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-908">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-910" />} data-id="element-909">
            Esporta
          </Button>
          <PermissionGate module="clients" action="create" data-id="element-911">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-913" />} data-id="element-912">
              Nuova anagrafica
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters & Search */}
      <Card className="mb-6" noPadding data-id="element-914">
        <div className="p-4 flex flex-col gap-4" data-id="element-915">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-916">
            <div className="flex-1" data-id="element-917">
              <Input placeholder="Cerca per ragione sociale, P.IVA, referente..." startContent={<Search className="h-4 w-4" data-id="element-919" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-918" />
            </div>
            <div className="flex gap-2" data-id="element-920">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-922" />} data-id="element-921">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-923">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="clients" data-id="element-924" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-925">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti gli stati'
          }, {
            value: 'active',
            label: 'Attivo'
          }, {
            value: 'prospect',
            label: 'Prospect'
          }, {
            value: 'inactive',
            label: 'Non attivo'
          }, {
            value: 'draft',
            label: 'Bozza'
          }]} data-id="element-926" />
              <Select label="Zona" value={filters.zone || ''} onChange={e => setFilter('zone', e.target.value)} options={[{
            value: '',
            label: 'Tutte le zone'
          }, {
            value: 'arezzo',
            label: 'Arezzo'
          }, {
            value: 'valdarno',
            label: 'Valdarno'
          }, {
            value: 'casentino',
            label: 'Casentino'
          }, {
            value: 'firenze',
            label: 'Firenze'
          }]} data-id="element-927" />
              <div className="col-span-full flex justify-end" data-id="element-928">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-930" />} className="font-medium" data-id="element-929">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {/* Data Table */}
      <Card noPadding className="overflow-hidden" data-id="element-931">
        <div className="overflow-x-auto" data-id="element-932">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-933">
            <thead data-id="element-934">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-935">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-32' : ''} ${col.key === 'number' ? 'w-32' : ''} ${col.key === 'name' ? 'w-auto' : ''}`} data-id="element-936">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-937">
              {isSearching ? <tr data-id="element-938">
                  <td colSpan={visibleColumns.length} className="px-6 py-12 text-center text-slate-500" data-id="element-939">
                    <div className="flex justify-center items-center gap-2" data-id="element-940">
                      <div className="animate-spin h-5 w-5 border-2 border-seform-orange border-t-transparent rounded-full" data-id="element-941"></div>
                      Ricerca in corso...
                    </div>
                  </td>
                </tr> : paginatedData.length > 0 ? paginatedData.map(client => <tr key={client.id} className="hover:bg-slate-50/80 transition-colors group cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)} data-id="element-942">
                    {visibleColumns.includes('number') && <td className="px-6 py-4 text-sm font-mono text-slate-500" data-id="element-943">
                        {client.number}
                      </td>}
                    {visibleColumns.includes('name') && <td className="px-6 py-4" data-id="element-944">
                        <div className="flex items-center gap-3" data-id="element-945">
                          <div className="w-8 h-8 rounded-lg bg-seform-navy/5 flex items-center justify-center text-seform-navy font-bold text-xs" data-id="element-946">
                            {client.name.substring(0, 2).toUpperCase()}
                          </div>
                          <div data-id="element-947">
                            <p className="text-sm font-semibold text-slate-900" data-id="element-948">
                              {client.name}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-949">
                              {client.email}
                            </p>
                          </div>
                        </div>
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-950">
                        <div className="flex items-center gap-2" data-id="element-951">
                          <Badge variant={client.status === 'active' ? 'success' : client.status === 'prospect' ? 'warning' : client.status === 'draft' ? 'neutral' : 'neutral'} data-id="element-952">
                            {client.status === 'active' ? 'Attivo' : client.status === 'prospect' ? 'Prospect' : client.status === 'draft' ? 'Bozza' : 'Non attivo'}
                          </Badge>
                          {client.status === 'draft' && <Badge variant="warning" size="sm" data-id="element-953">
                              Bozza
                            </Badge>}
                        </div>
                      </td>}
                    {visibleColumns.includes('zone') && <td className="px-6 py-4" data-id="element-954">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-955">
                          <MapPin className="h-3.5 w-3.5 text-slate-400" data-id="element-956" />
                          {client.zone}
                        </div>
                      </td>}
                    {visibleColumns.includes('clientOf') && <td className="px-6 py-4" data-id="element-957">
                        <div className="flex gap-1 flex-wrap" data-id="element-958">
                          {client.clientOf?.map((s: string) => <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 capitalize" data-id="element-959">
                              {s.replace('_', ' ')}
                            </span>)}
                        </div>
                      </td>}
                    {visibleColumns.includes('safetyReferent') && <td className="px-6 py-4 text-sm text-slate-600 capitalize" data-id="element-960">
                        {client.safetyReferent}
                      </td>}
                    {visibleColumns.includes('employeesCount') && <td className="px-6 py-4 text-sm text-slate-600 font-mono" data-id="element-961">
                        {client.employeesCount}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()} data-id="element-962">
                        <div className="flex items-center justify-end gap-2" data-id="element-963">
                          <Button variant="light" size="sm" className="!px-2" onClick={() => navigate(`/clients/${client.id}`)} data-id="element-964">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-965" />
                          </Button>
                          <PermissionGate module="clients" action="edit" data-id="element-966">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(client)} data-id="element-967">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-968" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate module="clients" action="delete" data-id="element-969">
                            <Button variant="light" size="sm" className="!px-2" onClick={e => handleDeleteClick(e, client)} data-id="element-970">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-971" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>) : <tr data-id="element-972">
                  <td colSpan={visibleColumns.length} className="px-6 py-12 text-center text-slate-500" data-id="element-973">
                    Nessun cliente trovato
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-100" data-id="element-974">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-975" />
        </div>
      </Card>

      {/* Create/Edit Wizard Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingClient ? 'Modifica anagrafica' : 'Nuova anagrafica'} size="2xl" data-id="element-976">
        <AnimatePresence mode="wait" data-id="element-977">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center py-16" data-id="element-978">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-979">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-980" />
              </motion.div>
              <motion.p initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg font-semibold text-slate-800" data-id="element-981">
                Anagrafica salvata con successo
              </motion.p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col h-full" data-id="element-982">
              {/* Stepper */}
              <div className="mb-8 px-2" data-id="element-983">
                <div className="flex items-center justify-between relative" data-id="element-984">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 -z-10" data-id="element-985"></div>
                  {[1, 2, 3, 4, 5].map(step => <div key={step} className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                        ${step === currentStep ? 'bg-seform-orange text-white shadow-lg scale-110' : step < currentStep ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}
                      `} data-id="element-986">
                      {step < currentStep ? <CheckCircle2 className="h-5 w-5" data-id="element-987" /> : step}
                    </div>)}
                </div>
                <div className="flex justify-between mt-2 text-xs font-medium text-slate-500 px-1" data-id="element-988">
                  <span data-id="element-989">Dati base</span>
                  <span data-id="element-990">Indirizzi</span>
                  <span data-id="element-991">Contatti</span>
                  <span data-id="element-992">Fiscali</span>
                  <span data-id="element-993">Relazioni</span>
                </div>
              </div>

              {/* Step Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar px-1 py-2" data-id="element-994">
                {renderWizardStep()}
              </div>

              {/* Footer Actions */}
              <div className="flex justify-between items-center pt-6 mt-6 border-t border-slate-100" data-id="element-995">
                <Button variant="flat" color="default" onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : setIsModalOpen(false)} startContent={currentStep > 1 ? <ChevronLeft className="h-4 w-4" data-id="element-997" /> : undefined} data-id="element-996">
                  {currentStep > 1 ? 'Indietro' : 'Annulla'}
                </Button>

                <div className="flex gap-3" data-id="element-998">
                  <Button variant="light" size="sm" className="text-slate-400" data-id="element-999">
                    Salva bozza
                  </Button>
                  {currentStep < 5 ? <Button onClick={() => setCurrentStep(currentStep + 1)} endContent={<ChevronRight className="h-4 w-4" data-id="element-1001" />} data-id="element-1000">
                      Avanti
                    </Button> : <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1003" />} color="success" data-id="element-1002">
                      Completa e salva
                    </Button>}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-1004">
        <div className="space-y-6" data-id="element-1005">
          <div className="flex items-start gap-4" data-id="element-1006">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-1007">
              <Trash2 className="h-6 w-6 text-red-600" data-id="element-1008" />
            </div>
            <div data-id="element-1009">
              <p className="text-slate-700" data-id="element-1010">
                Sei sicuro di voler eliminare l'anagrafica{' '}
                <strong data-id="element-1011">{clientToDelete?.name}</strong>?
              </p>
              <p className="text-sm text-slate-500 mt-2" data-id="element-1012">
                Questa azione è irreversibile e rimuoverà tutti i dati
                associati.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1013">
            <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-1014">
              Annulla
            </Button>
            <Button color="danger" onClick={handleConfirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-1016" />} data-id="element-1015">
              Elimina
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>;
}